import type { SubscriptionTier } from '@/types';

export type PaidPlan = 'core' | 'family';

export interface TierConfig {
  readonly id: SubscriptionTier;
  readonly label: string;
  readonly priceLabel: string;
  /** Monthly email quota. null = no limit (Family). */
  readonly emailQuota: number | null;
  readonly tagline: string;
  readonly features: readonly string[];
}

// COPY-TODO: subtitles + feature copy are placeholder per the Plan
// design dev notes (Apr 30 changelog). George/Millie own the final
// pass before production; the strings here mirror the Figma frame so
// the layout is faithful to design until then.
export const TIERS: Record<SubscriptionTier, TierConfig> = {
  FREE: {
    id: 'FREE',
    label: 'Free',
    priceLabel: 'Free',
    emailQuota: 20,
    tagline: 'Perfect for getting started.',
    features: ['Up to 20 emails confirmed per month'],
  },
  CORE: {
    id: 'CORE',
    label: 'Core',
    priceLabel: '$7/month',
    emailQuota: 100,
    tagline: 'For the busy household.',
    features: ['Up to 100 emails confirmed per month'],
  },
  FAMILY: {
    id: 'FAMILY',
    label: 'Family',
    priceLabel: '$15/month',
    emailQuota: null,
    tagline: 'For households with a lot going on.',
    features: ['Unlimited emails confirmed per month'],
  },
};

/**
 * Plans the user can choose from the Billing page. FREE is the
 * default state, not a selectable plan.
 */
export const SELECTABLE_PLANS: readonly PaidPlan[] = ['core', 'family'];

/**
 * Reasons captured when a user schedules a downgrade or cancel. The
 * keys are persisted in `subscriptions.cancel_reason`; labels are
 * surfaced verbatim by the modal. Keep the order stable — modal
 * renders in this order.
 */
export const CANCEL_REASONS = [
  { key: 'not-useful', label: "I didn't find it useful" },
  { key: 'different-app', label: "I'm using a different app" },
  { key: 'too-expensive', label: 'It was too expensive' },
  { key: 'missing-feature', label: 'Missing a feature I need' },
  { key: 'rather-not-say', label: "I'd rather not say" },
  { key: 'other', label: 'Other...' },
] as const;

export type CancelReasonKey = (typeof CANCEL_REASONS)[number]['key'];

/**
 * The plan-limit sentence (YAR-478, approved copy): "emails confirmed" is the
 * plan count and the plan is named for people ("Free"), never the raw tier
 * value. Shared by Review's limit banner and `QuotaExceededError`, and
 * mirrored server-side in `_shared/tier-quotas.ts` for `emails-approve`.
 */
export function quotaExceededMessage(
  tier: SubscriptionTier,
  used: number,
  quota: number,
): string {
  const plan = TIERS[tier].label;
  return `You've confirmed ${used} of ${quota} emails on the ${plan} plan this month.`;
}
